import React, { useState } from 'react';
import iconCheck from '../../assets/check.svg';
import { formatEUR } from './constants';

// ─── ItemSelector ─────────────────────────────────────────────────────────────
// Sélecteur générique réutilisé dans deux contextes :
//   1. Remise par article (DiscountManager)         → mode="discount"
//   2. Ventilation par article (VentilationPanel)   → mode="partial"
//
// Dans les deux cas : tout à 0 par défaut, stepper par ligne,
// la ligne s'illumine dès qty >= 1.
//
// Props :
//   items        → [{ id, name, qty (disponible), price }]
//   mode         → 'discount' | 'partial'
//   discountType → '%' | '€' (pour affichage prix en mode discount)
//   onApply      → ([{ id, qty }]) => void
//   onClose      → () => void  (uniquement en mode overlay)
//   inline       → bool — si true, pas d'overlay (rendu inline dans le parent)

const ItemSelector = ({
    items,
    mode = 'discount',
    discountType = '%',
    onApply,
    onClose,
    inline = false,
}) => {
    // { [itemId]: qty sélectionnée } — 0 par défaut
    const [selections, setSelections] = useState(
        Object.fromEntries(items.map(i => [i.id, 0]))
    );
    const [search, setSearch] = useState('');

    const filtered = items.filter(i =>
        i.name.toLowerCase().includes(search.toLowerCase())
    );

    const setQty = (id, delta) => {
        setSelections(prev => {
            const max     = items.find(i => i.id === id)?.qty ?? 0;
            const current = prev[id] ?? 0;
            return { ...prev, [id]: Math.max(0, Math.min(max, current + delta)) };
        });
    };

    const handleApply = () => {
        const result = Object.entries(selections)
            .filter(([, qty]) => qty > 0)
            .map(([strId, qty]) => {
                const originalItem = items.find(i => String(i.id) === strId);
                return { id: originalItem ? originalItem.id : strId, qty };
            });
        onApply(result);
    };

    const anySelected   = Object.values(selections).some(q => q > 0);
    const totalSelected = Object.entries(selections).reduce((sum, [strId, qty]) => {
        const item = items.find(i => String(i.id) === strId);
        return item ? sum + item.price * qty : sum;
    }, 0);

    const applyLabel = mode === 'discount'
        ? 'Appliquer à/aux article(s)'
        : `Payer ces articles — ${formatEUR(totalSelected)}`;

    const content = (
        <div className={inline ? 'item-selector-inline' : 'selector-window'}>
            <input
                type="text"
                className="white-input"
                placeholder="Rechercher un article…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus={!inline}
            />

            <div className="selector-list">
                <div className="selector-header">
                    {mode === 'discount' ? 'Sélectionner les unités' : 'Articles à régler'}
                </div>

                {filtered.length === 0 && (
                    <p className="text-muted history-empty">Aucun article disponible</p>
                )}

                {filtered.map(item => {
                    const selectedQty = selections[item.id] ?? 0;
                    const isActive    = selectedQty > 0;

                    return (
                        <div
                            key={item.id}
                            className={`selector-item ${isActive ? 'active' : ''}`}
                        >
                            <div className="selector-item-left">
                                <div className="qty-stepper mini">
                                    <button onClick={() => setQty(item.id, -1)}>&minus;</button>
                                    <span>{selectedQty}</span>
                                    <button onClick={() => setQty(item.id, +1)}>&#43;</button>
                                </div>
                                <div className="selector-item-info">
                                    <span>{item.name}</span>
                                    <span className="selector-item-sub">
                                        {selectedQty > 0
                                            ? `${selectedQty} / ${item.qty} unité${item.qty > 1 ? 's' : ''}`
                                            : `${item.qty} disponible${item.qty > 1 ? 's' : ''}`}
                                    </span>
                                </div>
                            </div>
                            <span className="selector-item-price">
                                {formatEUR(item.price * selectedQty)}
                            </span>
                        </div>
                    );
                })}
            </div>

            <button
                className={`pill-btn ${anySelected ? 'blue' : 'outline'} w-full mt-md`}
                onClick={handleApply}
                disabled={!anySelected}
            >
                {applyLabel}
                <img src={iconCheck} alt="" className="white_svg size-16 ml-auto" />
            </button>

            {!inline && (
                <button className="pill-btn outline w-full mt-sm" onClick={onClose}>
                    Annuler
                </button>
            )}
        </div>
    );

    if (inline) return content;

    return (
        <div className="selector-overlay">
            {content}
        </div>
    );
};

export default ItemSelector;
